import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import type { UserStateEntry, UserStateMap, UserStateItemKey } from "../../../types/user-state";
import { sanitizeUserStateEntries } from "../../../utils/user-state/sanitize-user-state-map";

export interface UserStateSliceState {
  entries: UserStateMap;
  hydratedFromSSR: boolean;
}

const initialState: UserStateSliceState = {
  entries: {},
  hydratedFromSSR: false,
};

const userStateSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
    hydrateFromSSR(state, action: PayloadAction<UserStateMap>) {
      const { sanitized, droppedKeys } = sanitizeUserStateEntries(action.payload);
      if (import.meta.env.DEV && droppedKeys.length > 0) {
        console.warn("[userState] hydrateFromSSR dropped invalid keys:", droppedKeys);
      }
      state.entries = { ...sanitized };
      state.hydratedFromSSR = true;
    },
    mergeEntries(state, action: PayloadAction<UserStateMap>) {
      const { sanitized, droppedKeys } = sanitizeUserStateEntries(action.payload);
      if (import.meta.env.DEV && droppedKeys.length > 0) {
        console.warn("[userState] mergeEntries dropped invalid keys:", droppedKeys);
      }
      state.entries = {
        ...state.entries,
        ...sanitized,
      };
    },
    setEntry(
      state,
      action: PayloadAction<{ itemKey: UserStateItemKey; entry: UserStateEntry }>
    ) {
      state.entries[action.payload.itemKey] = action.payload.entry;
    },
    replaceAll(state, action: PayloadAction<UserStateMap>) {
      const { sanitized, droppedKeys } = sanitizeUserStateEntries(action.payload);
      if (import.meta.env.DEV && droppedKeys.length > 0) {
        console.warn("[userState] replaceAll dropped invalid keys:", droppedKeys);
      }
      state.entries = { ...sanitized };
    },
    removeEntry(state, action: PayloadAction<UserStateItemKey>) {
      delete state.entries[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      const incoming = action.payload?.userState as UserStateSliceState | undefined;
      if (!incoming?.entries) {
        return;
      }
      const { sanitized, droppedKeys } = sanitizeUserStateEntries(incoming.entries);
      if (import.meta.env.DEV && droppedKeys.length > 0) {
        console.warn("[userState] REHYDRATE dropped invalid persisted keys:", droppedKeys);
      }
      state.entries = {
        ...sanitized,
        ...state.entries,
      };
    });
  },
});

export const { hydrateFromSSR, mergeEntries, setEntry, replaceAll, removeEntry } = userStateSlice.actions;
export const userStateReducer = userStateSlice.reducer;

export const userStateSelectors = {
  selectAll: (state: UserStateSliceState["entries"]) => state,
  selectByKey: (state: UserStateSliceState["entries"], itemKey: UserStateItemKey) =>
    state[itemKey],
};

